import { Cancel, CheckCircle, Groups, LibraryAddCheck, LocalActivity } from '@mui/icons-material';
import { Alert, Snackbar } from '@mui/material';
import { Timestamp } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import Colors from "../colors";
import LoadingSpinner from "../components/LoadingSpinner";
import NavTabs from "../components/NavTabs";
import PlusIconButton from "../components/PlusIconButton";
import SearchBar from "../components/SearchBar";
import SelectableCard from "../components/SelectableCard";
import SelectableCardContentRow from "../components/SelectableCardContentRow";
import { createNewDocInDb, getDocByCollectionAndId, getDocsByCollectionAndFK, getDocsBySubCollectionAndIds } from "../firebase";
import EncourageCreation from '../components/EncourageCreation';

function SafetyAudit({ user }) {
  const { formId } = useParams();
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [audits, setAudits] = useState([]);
  const [filteredAudits, setFilteredAudits] = useState([]);
  const navigate = useNavigate();
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [isArchived, setIsArchived] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleCreateAudit = async () => {
    const firestoreTimestamp = Timestamp.now();
    const emptyAudit = {
      formId: formId,
      formMemberIds: [],
      createdAt: firestoreTimestamp,
      updatedAt: firestoreTimestamp,
      activity: ''
    };
    const doc = await createNewDocInDb('SafetyAudits', emptyAudit);
    navigate(`/spot-checks/${doc.id}`, { state: { data: doc } });
  };


  useEffect(() => {
    if (!user) return;
    const fetchChecks = (safetyAuditId) => {
      return new Promise(async (resolve, reject) => {
        try {
          const docs = await getDocsByCollectionAndFK("SpotChecks", 'safetyAuditId', safetyAuditId);
          const compliantArray = [];
          docs.forEach(doc => {
            const newObj = {
              compliant: doc.compliant,
              time: doc.time.toDate(),
            }
            compliantArray.push(newObj);
          });
          const sortedRows = compliantArray.sort((a, b) => b.time - a.time);
          resolve(sortedRows);
        } catch (err) {
          reject(err);
        }
      });
    };


    const fetchAudits = async () => {
      const docs = await getDocsByCollectionAndFK("SafetyAudits", 'formId', formId);
      const form = await getDocByCollectionAndId("Forms", formId);
      setIsArchived(form.isArchived || form.userId !== user.uid);
      setSnackbarOpen(form.isArchived || form.userId !== user.uid);
      const auditsList = [];
      for (const doc of docs) {
        const checks = await fetchChecks(doc.id);
        const members = await getDocsBySubCollectionAndIds('Forms', formId, 'FormMembers', doc.formMemberIds);
        const memberNames = members.map((member) => ({ firstName: member.firstName, lastName: member.lastName }));
        const newObj = {
          id: doc.id,
          checks,
          activity: doc.activity,
          members: memberNames,
          updatedAt: doc.updatedAt.toDate(),
        }
        auditsList.push(newObj);
      }
      const sortedRows = auditsList.sort((a, b) => b.updatedAt - a.updatedAt);
      setAudits(sortedRows);
      setFilteredAudits(sortedRows);
      setLoadingStatus(false);
    };
    fetchAudits();
  }, [formId, user]);


  useEffect(() => {
    let filteredObjs = audits;
    if (tab === 1) {
      filteredObjs = filteredObjs.filter((audit) => {
        const nonCompliantChecks = audit.checks.filter((check) => !check.compliant);
        return nonCompliantChecks.length > 0;
      });
    } else if (tab === 2) {
      filteredObjs = filteredObjs.filter((audit) => {
        const nonCompliantChecks = audit.checks.filter((check) => !check.compliant);
        return nonCompliantChecks.length === 0;
      });
    }

    if (search.length > 0) {
      filteredObjs = filteredObjs.filter((audit) => {
        return audit.activity.toLowerCase().includes(search.toLowerCase());
      });
    }
    setFilteredAudits(filteredObjs);
  }, [tab, audits, search]);

  const tabStyles = {
    marginTop: '20px',
    '&.Mui-selected': {
      background: `${Colors.blue} !important`,
      border: `1px ${Colors.blue} solid !important`,
      color: Colors.white,
      fontWeight: '600',
    },
    '&:hover': {
      background: Colors.lightBlue2,
    },
    '&.Mui-focusVisible': {
      backgroundColor: Colors.lightBlue2,
    },
  }

  const containerStyles = {
    paddingTop: '25px',
    textAlign: 'center'
  }

  const getIcon = (isCompliant, key) => {
    if (isCompliant) return <CheckCircle sx={{ color: Colors.green }} key={key} />;
    else return <Cancel sx={{ color: Colors.red }} key={key} />;
  }

  const getMembersMainText = (membersList) => {
    const max = 5;
    let membersStr = membersList.map((m, i) => {
      return `${m.firstName} ${m.lastName[0]}`
    }).slice(0, max).join(', ')
    const diff = membersList.length - max;
    if (diff > 1) membersStr += `, and ${diff} others`;
    else if (diff > 0) membersStr += `, and ${diff} other`;
    return membersStr;
  }


  return (
    <div style={containerStyles}>
      <Snackbar open={snackbarOpen} autoHideDuration={10000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ marginTop: '50px' }}>
        <Alert severity="info" sx={{ width: '100%' }}>
          These Audits are archived or you do not have permission to edit it.
        </Alert>
      </Snackbar>
      <SearchBar search={search} setSearch={setSearch} placeholder="Search by Name or Activity" />
      <NavTabs tabTitles={['All', 'Noncompliant', 'Compliant']} style={tabStyles} value={tab} setValue={setTab} />
      <LoadingSpinner isLoading={loadingStatus} />
      {audits.length === 0 && !isArchived && <EncourageCreation headerText={'(0) Safety Audits found.'} suggestionText={'Tap the plus icon to get started!'} borderColor={Colors.blue} />}
      {audits.length !== 0 && filteredAudits.length === 0 && <EncourageCreation headerText={'(0) Safety Audits found.'} suggestionText={'Try clearing filters to see all results!'} borderColor={Colors.blue} />}
      {audits.length === 0 && isArchived && <EncourageCreation headerText={'(0) Safety Audits found.'} borderColor={Colors.blue} />}
      {
        filteredAudits.map((audit) => (
          <SelectableCard
            showEditIcon={!isArchived}
            key={audit.id}
            onClickHandler={() => navigate(`/spot-checks/${audit.id}`, { state: { data: audit } })}
            infoRows={[
              <SelectableCardContentRow iconComponent={<Groups sx={{ fontSize: '43px' }} />} title={'members'} mainText={getMembersMainText(audit.members)} key={audit.id + '1'} />,
              <SelectableCardContentRow iconComponent={<LocalActivity sx={{ fontSize: '42px', color: Colors.blue }} />} title={'activity'} mainText={audit.activity} key={audit.id + '2'} />,
              <SelectableCardContentRow iconComponent={<LibraryAddCheck sx={{ fontSize: '42px', color: Colors.green }} />} title={'checks'}
                mainText={audit.checks.length > 0 ? <>{audit.checks.map((check, i) => getIcon(check.compliant, i))}</> : ''} key={audit.id + '3'} />
            ]}
            borderColor={Colors.blue} />
        ))
      }
      {!isArchived &&
        <div
          style={{ position: 'fixed', bottom: '20px', right: '20px' }} >
          <PlusIconButton color={Colors.blue} onClick={handleCreateAudit} />
        </div>
      }

    </div>
  )
}

export default SafetyAudit;